import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { MAX_LINES, capQuantity } from '../lib/shop/orderLimits'

export interface CartItem {
  productId: string
  /** Null for a product that sells as a single SKU (no variants). */
  variantId: string | null
  sku: string
  name: string
  quantity: number
}

/** A cart line is identified by product *and* variant, so two sizes of one
 *  product stay separate lines. */
export const lineKey = (item: Pick<CartItem, 'productId' | 'variantId'>): string =>
  `${item.productId}:${item.variantId ?? ''}`

interface CartState {
  items: CartItem[]
  addItem: (item: Omit<CartItem, 'quantity'>) => void
  removeItem: (key: string) => void
  updateQuantity: (key: string, quantity: number) => void
  clearCart: () => void
  cartCount: () => number
  /** BUG-518: the discount code the shopper typed or applied ('' for none), shared by basket and
   *  checkout and kept with the basket, so it is never asked for twice. */
  discountCode: string
  setDiscountCode: (code: string) => void
}

export const useCartStore = create<CartState>()(
  persist(
    (set, get) => ({
      items: [],

      // BUG-473: the order API takes at most 20 of one item and 30 lines, so the basket never holds more.
      addItem: (item) =>
        set((state) => {
          const key = lineKey(item)
          const existing = state.items.find((i) => lineKey(i) === key)
          if (existing) {
            return {
              items: state.items.map((i) =>
                lineKey(i) === key ? { ...i, quantity: capQuantity(i.quantity + 1) } : i,
              ),
            }
          }
          if (state.items.length >= MAX_LINES) return state
          return { items: [...state.items, { ...item, quantity: 1 }] }
        }),

      removeItem: (key) =>
        set((state) => ({
          items: state.items.filter((i) => lineKey(i) !== key),
        })),

      updateQuantity: (key, quantity) =>
        set((state) => ({
          items:
            quantity <= 0
              ? state.items.filter((i) => lineKey(i) !== key)
              : state.items.map((i) => (lineKey(i) === key ? { ...i, quantity: capQuantity(quantity) } : i)),
        })),

      // A paid order empties the basket and its discount code together.
      clearCart: () => set({ items: [], discountCode: '' }),

      discountCode: '',
      setDiscountCode: (code) => set({ discountCode: code }),

      cartCount: () => get().items.reduce((sum, i) => sum + i.quantity, 0),
    }),
    {
      name: 'helin-webshop-cart',
      // Hydration (#418): prerendered pages ship an empty basket badge, so the saved basket must not
      // load before React has hydrated them. CartRehydrate loads it in an effect (main.tsx).
      skipHydration: true,
      // A basket saved before the limits existed is brought inside them as it loads.
      merge: (persisted, current) => {
        const saved = persisted as Partial<CartState> | undefined
        const items = saved?.items
        if (!Array.isArray(items)) return current
        return {
          ...current,
          items: items.slice(0, MAX_LINES).map((i) => ({ ...i, quantity: capQuantity(i.quantity) })),
          discountCode: typeof saved?.discountCode === 'string' ? saved.discountCode : '',
        }
      },
    },
  ),
)
